import { AppThunkAction } from '..';
import { CallApiWithNotification } from 'src/clients/ApiService';
import IntakeFormsClient from 'src/clients/IntakeFormsClient';
import ApiUtils from 'src/utils/ApiUtils';
import { mapUserInstanceIntoUserEntityFields } from 'src/utils/UserUtils';
import {
  IntakeForm,
  IntakeFormResponse,
  SET_FORM_STATE_REQUEST,
  INSERT_INTAKE_FORMS,
  SET_FORM_STATE_UPDATED,
  SET_RESPONSE_STATE_REQUEST,
  SET_RESPONSE_STATE_ADDED,
  SET_RESPONSE_STATE_FAIL,
  SET_RESPONSE_STATE_UPDATED,
  SET_RESPONSE_PAGE_STATE,
  ResponsePageState,
  SET_FORM_CREATE_PANEL,
  FormCreatePanelType,
  SET_USER_RESPONSE_STATE_ADDED,
  SET_USER_RESPONSE_STATE_FAIL,
  SET_USER_RESPONSE_STATE_REQUEST,
  DELETE_INTAKE_FORM_SUCCESS,
  DELETE_INTAKE_FORM_FAIL,
  UPDATE_INTAKE_FORMS,
  DELETE_RESPONSES_SUCCESS,
} from 'src/store/intake_forms/types';
import { QuestionTemplate } from 'src/components/IntakeForms/intakeFormTypes';
import history from 'src/history';

// add a template direct to the store without making a http call to the api
export const addFormTemplatesToStore = (formTemplates: IntakeForm[]) => ({
  type: INSERT_INTAKE_FORMS,
  payload: formTemplates,
});

export const loadIntakeForms = (): AppThunkAction => async dispatch => {
  function request() {
    return { type: SET_FORM_STATE_REQUEST };
  }

  dispatch(request());

  try {
    const result = await IntakeFormsClient.LoadIntakeForm();
    dispatch(addFormTemplatesToStore(result));
  } catch (ex) {
    console.error('Error while loading intake forms', ex);
  }
};

export const saveIntakeForm = (data: IntakeForm): AppThunkAction => async (
  dispatch,
  getState,
) => {
  let result;

  function request() {
    return { type: SET_FORM_STATE_REQUEST };
  }

  dispatch(request());
  try {
    result = await CallApiWithNotification({
      executeFunction: IntakeFormsClient.SaveIntakeForm,
      checkFunction: ApiUtils.IsBatchCreateResultSuccessful,
      params: data,
      successMessage: `${data.fields.name} form has been created.`,
      errorMessage: `${data.fields.name} could not be created.`,
      dispatch,
    });

    if (result && result.status) {
      const createdForm = result.data.createdItems[0];
      const userInstance = getState().user.instance;
      const ownerStaged = mapUserInstanceIntoUserEntityFields(userInstance);

      dispatch(
        addFormTemplatesToStore([
          {
            ...createdForm,
            owner: {
              fields: {
                ...ownerStaged,
              },
            },
          },
        ]),
      );
    }
  } catch (ex) {
    console.error('Error while saving intake forms', ex);
  }

  return result;
};

export const updateIntakeForm = (
  data: IntakeForm,
): AppThunkAction => async dispatch => {
  let result;

  function request() {
    return { type: SET_FORM_STATE_REQUEST };
  }

  function success(payload: IntakeForm) {
    return { type: SET_FORM_STATE_UPDATED, payload };
  }

  dispatch(request());

  try {
    result = await CallApiWithNotification({
      executeFunction: IntakeFormsClient.UpdateIntakeForm,
      checkFunction: ApiUtils.IsBatchCreateResultSuccessful,
      params: data,
      successMessage: `${data.fields.name} form has been updated.`,
      errorMessage: `${data.fields.name} could not be updated.`,
      dispatch,
    });

    dispatch(success(result.data.createdItems[0]));
  } catch (ex) {
    console.error('Error while updating intake form', ex);
  }
  return result;
};

export const sendIntakeForm = (
  clientsIds: string[],
  formId: string,
): AppThunkAction => async dispatch => {
  function request() {
    return { type: SET_FORM_STATE_REQUEST };
  }

  function success(payload: IntakeForm[]) {
    return { type: SET_FORM_STATE_UPDATED, payload };
  }

  dispatch(request());

  try {
    const result = await CallApiWithNotification({
      executeFunction: IntakeFormsClient.SendIntakeForm,
      checkFunction: ApiUtils.IsBatchCreateResultSuccessful,
      params: {
        clientsIds,
        formId,
      },
      successMessage: `Form has been sent.`,
      errorMessage: `Form could not be sent.`,
      dispatch,
    });
    dispatch(success(result.data.createdItems[0]));
  } catch (ex) {
    console.error('Error occurred while sending intake form', ex);
  }
};

export const duplicateIntakeForm = (
  name: string,
  formId: string,
): AppThunkAction => async (dispatch, getState) => {
  function redirectToFormEdit(id: string) {
    history.push(`/forms/edit?id=${id}`);
  }

  function request() {
    return { type: SET_FORM_STATE_REQUEST };
  }

  dispatch(request());

  try {
    const result = await CallApiWithNotification({
      executeFunction: IntakeFormsClient.DuplicateForm,
      checkFunction: ApiUtils.IsBatchCreateResultSuccessful,
      params: {
        name,
        formId,
      },
      successMessage: `${name} form has been created.`,
      errorMessage: `${name} could not be created.`,
      dispatch,
    });

    if (result && result.status) {
      const createdForm = result.data.createdItems[0];
      const userInstance = getState().user.instance;
      const ownerStaged = mapUserInstanceIntoUserEntityFields(userInstance);

      dispatch(
        addFormTemplatesToStore([
          {
            ...createdForm,
            owner: {
              fields: {
                ...ownerStaged,
              },
            },
          },
        ]),
      );

      redirectToFormEdit(createdForm.id);
    }
  } catch (ex) {
    console.error('Error when duplicating forms', ex);
  }
};

export const getIntakeFormResponseChannels = (
  formId: string,
): AppThunkAction => async dispatch => {
  function request() {
    return { type: SET_RESPONSE_STATE_REQUEST };
  }

  function success(payload: IntakeFormResponse[]) {
    return { type: SET_RESPONSE_STATE_ADDED, payload };
  }

  function failure(error: string) {
    return { type: SET_RESPONSE_STATE_FAIL, error };
  }

  dispatch(request());

  try {
    const result =
      (await IntakeFormsClient.GetIntakeFormResponseChannels(formId)) || [];
    if (result) {
      dispatch(success(result));
    }
  } catch (e) {
    const error = e as { message: string };
    dispatch(failure(error.message));
  }
};

export const getIntakeFormResponseByUserId = (
  userId: string,
): AppThunkAction => async dispatch => {
  function request() {
    return { type: SET_USER_RESPONSE_STATE_REQUEST };
  }

  function success(payload: IntakeFormResponse[]) {
    return { type: SET_USER_RESPONSE_STATE_ADDED, payload };
  }

  function failure(error: string) {
    return { type: SET_USER_RESPONSE_STATE_FAIL, error };
  }

  dispatch(request());

  try {
    const result = await IntakeFormsClient.GetIntakeFormResponseByUserId(
      userId,
    );
    dispatch(success(result));
  } catch (e) {
    const error = e as { message: string };
    dispatch(failure(error.message));
  }
};

export const loadIntakeFormResponses = (): AppThunkAction => async dispatch => {
  function request() {
    return { type: SET_RESPONSE_STATE_REQUEST };
  }

  function success(payload: IntakeForm[]) {
    return { type: SET_RESPONSE_STATE_ADDED, payload };
  }

  function failure(error: string) {
    return { type: SET_RESPONSE_STATE_FAIL, error };
  }

  dispatch(request());

  try {
    const result = await IntakeFormsClient.LoadIntakeFormResponses();
    dispatch(success(result));
  } catch (e) {
    const error = e as { message: string };
    dispatch(failure(error.message));
  }
};

export const setResponsePageState = (
  newResponsePageState: ResponsePageState,
): AppThunkAction => async dispatch => {
  dispatch({
    type: SET_RESPONSE_PAGE_STATE,
    payload: newResponsePageState,
  });
};

export const addFormResponses = (
  formResponse: IntakeForm[],
): AppThunkAction => dispatch => {
  dispatch({ type: SET_RESPONSE_STATE_ADDED, payload: formResponse });
};

export const updateIntakeFormResponses = (
  formId: string,
  questions: QuestionTemplate[],
): AppThunkAction => async dispatch => {
  function request() {
    return { type: SET_RESPONSE_STATE_REQUEST };
  }

  function success(payload: IntakeForm[]) {
    return { type: SET_RESPONSE_STATE_UPDATED, payload };
  }

  function failure(error: string) {
    return { type: SET_RESPONSE_STATE_FAIL, error };
  }

  dispatch(request());

  const formattedResponses = {
    id: formId,
    fields: {
      questions: questions.map(question => ({
        inputId: question.inputId,
        responseValue: question.responseValue || null,
      })),
    },
  };

  const result = await CallApiWithNotification({
    executeFunction: IntakeFormsClient.UpdateIntakeFormResponses,
    checkFunction: ApiUtils.IsBatchUpdateResultSuccessful,
    params: formattedResponses,
    successMessage: `Form answers have been successfully submitted.`,
    errorMessage: `Submitting form answers has failed.`,
    dispatch,
  });
  if (result.status) {
    dispatch(success(result.data.updatedItems[0]));
  } else {
    dispatch(failure(result.errorMessage));
  }

  return result;
};

export const setFormCreatePanel = (
  data: FormCreatePanelType,
): AppThunkAction => dispatch => {
  dispatch({
    type: SET_FORM_CREATE_PANEL,
    payload: data,
  });
};

export const deleteIntakeForm = (
  form: IntakeForm,
): AppThunkAction => async dispatch => {
  function success(payload: string[]) {
    return { type: DELETE_INTAKE_FORM_SUCCESS, payload };
  }

  function failure(error: string) {
    return { type: DELETE_INTAKE_FORM_FAIL, error };
  }

  const result = await CallApiWithNotification({
    executeFunction: IntakeFormsClient.DeleteIntakeForm,
    checkFunction: ApiUtils.IsBatchUpdateResultSuccessful,
    params: form,
    successMessage: `Form ${
      form ? `${form.fields.name}` : ''
    } has been deleted.`,
    errorMessage: `This form could not be deleted.`,
    dispatch,
  });

  if (result.status) {
    dispatch(success(result.data.succeededIds)); // TODO fix return type on DeleteIntakeForm as it is wrong
  } else {
    dispatch(failure(result.data));
  }
};

// remove a template direct to the store without making a http call to the api
export const removeFormTemplateFromStore = (formTemplate: IntakeForm[]) => ({
  type: DELETE_INTAKE_FORM_SUCCESS,
  payload: formTemplate.map(f => f.id),
});

// putFormTemplate a template direct to the store without making a http call to the api
export const updateFormTemplateFromStore = (payload: IntakeForm[]) => ({
  type: UPDATE_INTAKE_FORMS,
  payload,
});

// deleteFormResponses a form response direct to the store without making a http call to the api
export const deleteFormResponses = (responses: IntakeFormResponse[]) => ({
  type: DELETE_RESPONSES_SUCCESS,
  payload: responses.map(r => r.id),
});
